import { useTranslation } from "react-i18next";

import { embedSnippet } from "@/embed/snippet";
import { useShellNav } from "@/shell/useShellNav";

/**
 * "You already have a website? Add the quoting tool to it."
 *
 * The cheapest way in for a shop that is not going to move its site,
 * and until KAN-910 the landing mentioned it in half a sentence buried
 * in step two. Competitors put the same widget behind their top tier
 * (CutQuote sells it on Business); here it is on every plan including
 * Free, because gating an acquisition tool throttles exactly what it
 * exists to start — see the note on ``salesChannels/EmbedTask``.
 *
 * The snippet is generated by {@link embedSnippet}, the same function
 * that hands it to a tenant in the admin. Retyping it here would let
 * the marketing page advertise a snippet the product does not produce.
 */
export function PlatformEmbed({ lang }: { lang: string }): JSX.Element {
  const { t } = useTranslation("common");
  const { buildTenantUrl } = useShellNav();
  // No slug → the default tenant, i.e. the demo shop. Same helper the
  // rest of the marketing shell uses to cross into a storefront.
  //
  // buildtenanturl-ok: nothing is navigated to and no session is
  // involved. Only the ORIGIN is taken, to print inside a code sample
  // the visitor copies into their own site — where the widget then
  // loads for their anonymous customers. A session handover would have
  // nothing to hand over and nowhere to hand it.
  const demoOrigin = new URL(buildTenantUrl("/", null)).origin;
  const snippet = embedSnippet(demoOrigin, lang);
  // The design highlights the widget address inside the sample. Split on
  // it rather than rebuilding the string, so the text stays exactly what
  // ``embedSnippet`` produced.
  const [before, ...rest] = snippet.split(demoOrigin);
  const after = rest.join(demoOrigin);

  return (
    <section className="section ink" data-testid="platform-embed">
      <div className="inner">
        <div className="embed-grid">
          <div>
            <p className="kicker">{t("platformLanding.embed.kicker")}</p>
            <h2 className="h2" style={{ maxWidth: 420 }}>
              {t("platformLanding.embed.heading")}
            </h2>
            <p className="lead" style={{ margin: "24px 0 0", maxWidth: 520 }}>
              {t("platformLanding.embed.body")}
            </p>
            <p className="note">{t("platformLanding.embed.note")}</p>
          </div>

          {/* Collapsed by default (KAN-1946): a shop owner forwards the
              two lines to whoever runs their site and never needs to read
              them. Everything stays in the DOM, so the snippet is still
              what ``embedSnippet`` produced. */}
          <details className="code-card">
            <summary className="code-head">
              {t("platformLanding.embed.snippetLabel")}
            </summary>
            {/* Long URLs wrap instead of scrolling, so a phone shows the
                whole line rather than a clipped ``…/widge``. */}
            <pre className="code-body">
              <code data-testid="platform-embed-snippet">
                {rest.length > 0 ? (
                  <>
                    {before}
                    <b>{demoOrigin}</b>
                    {after}
                  </>
                ) : (
                  snippet
                )}
              </code>
            </pre>
          </details>
        </div>
      </div>
    </section>
  );
}
